/**
 * RequestProject — the public "Request a Project" wizard.
 *
 * Distinct from EditProjectDialog (the PMO admin form, 25 fields).
 * This is the lightweight intake any authenticated business user can
 * submit when they have a project idea but don't know — and shouldn't
 * have to know — the PMO taxonomy (status, t-shirt, hours, dates,
 * spec milestones, etc.). PMO fills those at triage.
 *
 * One page (Brett, 2026-10-22: "simpler — one page vs tabbing through
 * sections"): every question visible at once, required ones marked, one
 * Submit. Was five steps until then.
 * Submits to POST /portfolio/requests → creates a Requested project,
 * lands in New / unscoped on the Top Projects board where the PMO acts on it.
 *
 * v1 scope: no file uploads (supporting URLs only), no draft save,
 * sponsor is free text (auto-attribution waits for Phase 1 auth).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { SponsorPicker } from "@/components/ui/SponsorPicker";
import { cn } from "@/lib/cn";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface RequestForm {
  // Submitter identity — captured first so PMO has someone to reply to
  // at triage. Persisted as requested_by_name / requested_by_email on
  // the project row; will be auto-filled from the signed-in user once
  // Phase 1 auth lands. Free text for v1.
  requested_by_name: string;
  requested_by_email: string;
  name: string;
  summary: string;
  business_need: string;
  who_affected: string;
  success_criteria: string;
  sponsor: string;
  urgency: "Low" | "Medium" | "High";
  deadline: string;                  // ISO date OR ""
  no_deadline: boolean;
  supporting_links: string[];
}

const EMPTY_FORM: RequestForm = {
  requested_by_name: "",
  requested_by_email: "",
  name: "",
  summary: "",
  business_need: "",
  who_affected: "",
  success_criteria: "",
  sponsor: "",
  urgency: "Medium",
  deadline: "",
  no_deadline: false,
  supporting_links: [],
};

// Lightweight email shape check — server is the real validator, this just
// prevents an obviously-wrong submission from getting through Next.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RequestProject() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  // Submitter comes from the login; the planner knows who you are.
  const [form, setForm] = useState<RequestForm>(() => ({
    ...EMPTY_FORM,
    requested_by_name: user?.display_name ?? "",
    requested_by_email: user?.email ?? "",
  }));
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const submit = useMutation<{ id: string; name: string }, Error, RequestForm>({
    mutationFn: async (data) => {
      const payload = {
        name: data.name,
        summary: data.summary,
        business_need: data.business_need,
        who_affected: data.who_affected || null,
        success_criteria: data.success_criteria || null,
        sponsor: data.sponsor || null,
        urgency: data.urgency,
        deadline: data.no_deadline ? null : (data.deadline || null),
        supporting_links: data.supporting_links.filter((u) => u.trim().length > 0),
        requested_by_name: data.requested_by_name.trim() || null,
        requested_by_email: data.requested_by_email.trim() || null,
      };
      const resp = await api.post("/portfolio/requests", payload);
      return resp.data;
    },
    onSuccess: (proj) => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      setSubmittedId(proj.id);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? "Couldn't submit request");
    },
  });

  // Only the three required fields gate Submit; everything else is optional.
  const missing: string[] = [];
  if (!form.name.trim()) missing.push("Project name");
  if (!form.summary.trim()) missing.push("One-sentence summary");
  if (!form.business_need.trim()) missing.push("The problem you're solving");
  // A login email is enough for PMO to reply; a name is required only when there is no email.
  if (!form.requested_by_name.trim() && !form.requested_by_email.trim()) missing.push("Your name");
  const emailOk = form.requested_by_email.trim().length === 0 || EMAIL_RE.test(form.requested_by_email.trim());
  if (!emailOk) missing.push("A valid email");
  const ready = missing.length === 0;
  const trySubmit = () => {
    setTried(true);
    if (ready) submit.mutate(form);
  };

  // Confirmation view — replaces the wizard after a successful submit.
  if (submittedId) {
    return (
      <>
        <TopBar title="Request submitted" subtitle="Thanks — PMO will take it from here." />
        <div className="mx-auto max-w-2xl space-y-6 p-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl bg-surface p-8 ring-1 ring-emerald-200 dark:ring-emerald-500/30"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-ink">
                  Your request is in the queue
                </h2>
                <p className="mt-1 text-sm text-ink-3">
                  Your project request was logged as{" "}
                  <button
                    onClick={() => navigate(`/portfolio/${submittedId}`)}
                    className="font-mono font-semibold text-brand-navy-700 dark:text-brand-navy-300 hover:underline"
                  >
                    {submittedId}
                  </button>{" "}
                  and is now in Pending Approval.
                </p>
              </div>
            </div>
            <div className="mt-5 space-y-3 rounded-lg bg-canvas p-4 text-sm text-ink-2">
              <p className="font-medium text-ink">What happens next</p>
              <ul className="ml-1 list-inside list-disc space-y-1 text-ink-3">
                <li>PMO reviews requests on a regular cadence.</li>
                <li>It's now in New / unscoped on Top Projects. You'll hear back once it's approved, declined, or the PMO needs more info.</li>
                <li>You can track its status anytime at the project ID above.</li>
              </ul>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={() => navigate(`/portfolio/${submittedId}`)}
                className="rounded-md bg-brand-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy-700"
              >
                View my request
              </button>
              <button
                onClick={() => {
                  setForm({ ...EMPTY_FORM, requested_by_name: user?.display_name ?? "", requested_by_email: user?.email ?? "" });
                  setTried(false);
                  setSubmittedId(null);
                }}
                className="rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-ink-2 hover:bg-raised"
              >
                Submit another
              </button>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title="Request a Project" subtitle="One page. Three required answers, the rest optional. PMO does the rest." />
      <div className="mx-auto max-w-2xl p-8" data-testid="request-form">
        <div className="space-y-6 rounded-xl bg-surface p-6 ring-1 ring-line">
          <SubmitterLine form={form} setForm={setForm} knownUser={!!user?.email} />
          <Group title="The project">
            <StepWhat form={form} setForm={setForm} />
          </Group>
          <Group title="Why">
            <StepWhy form={form} setForm={setForm} />
          </Group>
          <Group title="When and who">
            <StepWhen form={form} setForm={setForm} />
          </Group>
          <Group title="Links" optional>
            <StepSupporting form={form} setForm={setForm} />
          </Group>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-muted" data-testid="request-missing">
            {ready
              ? "Lands in New / unscoped on Top Projects; PMO reviews from there."
              : tried
                ? `Still needed: ${missing.join(", ")}.`
                : `Required: ${missing.join(", ")}.`}
          </p>
          <button
            onClick={trySubmit}
            disabled={submit.isPending}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50",
              ready ? "bg-emerald-600 hover:bg-emerald-700" : "bg-ink-subtle hover:bg-ink-muted",
            )}
            data-testid="request-submit"
          >
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {submit.isPending ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </>
  );
}

/** Section heading on the one-page form. */
function Group({ title, optional, children }: { title: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <section className="border-t border-line-soft pt-5 first:border-t-0 first:pt-0">
      <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        {title}{optional && <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-subtle">optional</span>}
      </h2>
      {children}
    </section>
  );
}

/** "Submitting as …" — from the login; editable only when the account
 *  has no display name. */
function SubmitterLine({ form, setForm, knownUser }: { form: RequestForm; setForm: (f: RequestForm) => void; knownUser: boolean }) {
  if (knownUser) {
    return (
      <p className="text-xs text-ink-muted" data-testid="request-submitter">
        Submitting as <span className="font-medium text-ink-2">{form.requested_by_name || form.requested_by_email}</span>
        {form.requested_by_name && form.requested_by_email && <span> · {form.requested_by_email}</span>}
      </p>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Your name" required hint="So PMO knows who filed this.">
        <input type="text" value={form.requested_by_name} onChange={(e) => setForm({ ...form, requested_by_name: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Your email" hint="Where PMO replies if they need detail.">
        <input type="email" value={form.requested_by_email} onChange={(e) => setForm({ ...form, requested_by_email: e.target.value })} className={inputCls} />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step components — each one focused, plain-language, minimum required.
// ---------------------------------------------------------------------------

function Field({
  label, hint, required, children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {hint && <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300";

function StepWhat({
  form, setForm,
}: {
  form: RequestForm;
  setForm: (f: RequestForm) => void;
}) {
  return (
    <div className="space-y-5">
      <Field label="Project name" required hint="Short — what would you call it on a status report?">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Customer master data cleanup"
          className={inputCls}
        />
      </Field>
      <Field label="One-sentence summary" required hint="If we asked you in an elevator, what would this do?">
        <input
          type="text"
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
          placeholder="e.g. Dedupe and reconcile customer records across Salesforce and Syteline"
          className={inputCls}
        />
      </Field>
    </div>
  );
}

function StepWhy({
  form, setForm,
}: {
  form: RequestForm;
  setForm: (f: RequestForm) => void;
}) {
  return (
    <div className="space-y-5">
      <Field
        label="What problem are you trying to solve?"
        required
        hint="Be specific about the pain. Skip the solution for now. Use the toolbar for bullets, bold, or links."
      >
        <RichTextEditor
          compact
          rows={4}
          value={form.business_need}
          onChange={(v) => setForm({ ...form, business_need: v })}
          placeholder="e.g. CSRs spend ~20 min/day reconciling mismatched customer records, leading to wrong-address shipments and credit memos."
        />
      </Field>
      <Field label="Who's affected?" hint="Departments, teams, customers, vendors — whoever feels the pain.">
        <input
          type="text"
          value={form.who_affected}
          onChange={(e) => setForm({ ...form, who_affected: e.target.value })}
          placeholder="e.g. CSR team, AR, Sales ops"
          className={inputCls}
        />
      </Field>
      <Field
        label="What does success look like?"
        hint="One or two lines (or a bulleted list) about the outcome you want."
      >
        <RichTextEditor
          compact
          rows={3}
          value={form.success_criteria}
          onChange={(v) => setForm({ ...form, success_criteria: v })}
          placeholder="e.g. Zero duplicate customer records; CSR reconciliation time under 5 min/day."
        />
      </Field>
    </div>
  );
}

function StepWhen({
  form, setForm,
}: {
  form: RequestForm;
  setForm: (f: RequestForm) => void;
}) {
  return (
    <div className="space-y-5">
      <Field label="When do you need it by?" hint="A target date, if you have one — otherwise check the box below.">
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={form.deadline}
            onChange={(e) => setForm({ ...form, deadline: e.target.value, no_deadline: false })}
            disabled={form.no_deadline}
            className={cn(inputCls, "max-w-[200px]", form.no_deadline && "opacity-50")}
          />
          <label className="inline-flex items-center gap-2 text-xs text-ink-3">
            <input
              type="checkbox"
              checked={form.no_deadline}
              onChange={(e) => setForm({ ...form, no_deadline: e.target.checked, deadline: e.target.checked ? "" : form.deadline })}
              className="rounded border-line-strong"
            />
            No firm deadline
          </label>
        </div>
      </Field>
      <Field label="How urgent is this?" hint="Plain-English urgency. PMO will calibrate against the rest of the portfolio.">
        <div className="flex gap-2">
          {(["Low", "Medium", "High"] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setForm({ ...form, urgency: u })}
              className={cn(
                "flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                form.urgency === u
                  ? "border-brand-navy-500 bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-900 dark:text-brand-navy-300"
                  : "border-line bg-surface text-ink-3 hover:border-line-strong",
              )}
            >
              {u}
            </button>
          ))}
        </div>
      </Field>
      <Field
        label="Sponsor"
        hint="Pick the champion from existing sponsors, or use “+ Add new…” for someone not on the list. Leave “— none —” if you're not sure — PMO will sort it out."
      >
        {/* SponsorPicker — same control used on the initiative / project
            edit forms. Sources from the union of every sponsor already
            on a project or initiative, with an inline "+ Add new…"
            escape hatch so a request can introduce a sponsor that
            hasn't been tagged yet. Keeps spellings consistent (which
            is the whole point — Ray vs Raymond Trumble, etc.). */}
        <SponsorPicker
          value={form.sponsor || null}
          onChange={(v) => setForm({ ...form, sponsor: v ?? "" })}
          autoFocus={false}
          className={inputCls}
        />
      </Field>
    </div>
  );
}

function StepSupporting({
  form, setForm,
}: {
  form: RequestForm;
  setForm: (f: RequestForm) => void;
}) {
  const addLink = () => setForm({ ...form, supporting_links: [...form.supporting_links, ""] });
  const updateLink = (i: number, v: string) =>
    setForm({
      ...form,
      supporting_links: form.supporting_links.map((u, idx) => (idx === i ? v : u)),
    });
  const removeLink = (i: number) =>
    setForm({ ...form, supporting_links: form.supporting_links.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-5">
      <Field
        label="Related links"
        hint="Paste any Confluence pages, SharePoint docs, vendor URLs, or other context. Optional."
      >
        <div className="space-y-2">
          {form.supporting_links.map((url, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => updateLink(i, e.target.value)}
                placeholder="https://..."
                className={inputCls}
              />
              <button
                onClick={() => removeLink(i)}
                className="rounded-md p-1.5 text-ink-subtle hover:bg-raised hover:text-ink-2"
                title="Remove"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addLink}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line-strong px-3 py-1.5 text-xs font-medium text-ink-3 hover:border-brand-navy-400 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
          >
            <Plus className="h-3 w-3" />
            Add a link
          </button>
        </div>
      </Field>
      <p className="text-xs text-ink-muted">
        File uploads aren't supported yet — for now, share docs via a link
        (Confluence / SharePoint / Google Drive / etc.).
      </p>
    </div>
  );
}
